import { useAuth } from "@/auth/auth";
import type { components } from "@/client/backend/zz_schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import useSWR from "swr";

type Policy = components["schemas"]["SCIMPolicy"];

export function useSCIMConfiguration(organizationId: string, enabled: boolean) {
  const { client } = useAuth();
  return useSWR(enabled && organizationId !== "loading" ? `scim:${organizationId}` : null, async () => {
    const result = await client.GET("/organizations/{organizationId}/scim", {
      params: { path: { organizationId } },
    });
    if (result.error) throw new Error(result.error.message);
    return result.data;
  });
}

export function SCIMProvisioning({ organizationId }: { organizationId: string }) {
  const [lostAdmin, setLostAdmin] = useState(false);
  const [saved, setSaved] = useState(false);
  const { client } = useAuth();
  const {
    data: configuration,
    error: configurationError,
    mutate,
  } = useSCIMConfiguration(organizationId, !lostAdmin);
  const params = { path: { organizationId } };
  const [policy, setPolicy] = useState<Policy>();
  const [credential, setCredential] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [userStart, setUserStart] = useState(1);
  const [eventStart, setEventStart] = useState(1);

  // Policy changes invalidate the local draft; directory sync does not.
  useEffect(() => {
    setPolicy(undefined);
  }, [organizationId, configuration?.revision]);
  useEffect(() => {
    setCredential("");
    setLostAdmin(false);
    setSaved(false);
    setError("");
    setUserStart(1);
    setEventStart(1);
  }, [organizationId]);

  const {
    data: users,
    error: usersError,
    mutate: refreshUsers,
  } = useSWR(
    configuration?.configured && !lostAdmin ? ["scim-users", organizationId, userStart] : null,
    async () => {
      const r = await client.GET("/organizations/{organizationId}/scim/users", {
        params: { path: { organizationId }, query: { startIndex: userStart, count: 25 } },
      });
      if (r.error) throw new Error(r.error.message);
      return r.data;
    },
  );
  const {
    data: events,
    error: eventsError,
    mutate: refreshEvents,
  } = useSWR(
    configuration?.configured && !lostAdmin ? ["scim-events", organizationId, eventStart] : null,
    async () => {
      const r = await client.GET("/organizations/{organizationId}/scim/events", {
        params: { path: { organizationId }, query: { startIndex: eventStart, count: 25 } },
      });
      if (r.error) throw new Error(r.error.message);
      return r.data;
    },
  );

  const draft: Policy = {
    revision: policy?.revision ?? configuration?.revision ?? 0,
    directRoleAssignmentsDisabled: policy?.directRoleAssignmentsDisabled ?? configuration?.directRoleAssignmentsDisabled ?? false,
  };
  const edit = (next: Policy) => {
    setPolicy(next);
  };
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : "SCIM request failed");
    } finally {
      setBusy(false);
    }
  };
  const rotate = () =>
    run(async () => {
      const r = await client.POST("/organizations/{organizationId}/scim/credential", { params });
      if (r.error) throw new Error(r.error.message);
      setCredential(r.data.token);
      await mutate(r.data.configuration);
    });
  const revoke = () =>
    run(async () => {
      const r = await client.DELETE("/organizations/{organizationId}/scim/credential", { params });
      if (r.error) throw new Error(r.error.message);
      setCredential("");
      await mutate(r.data);
    });
  const applyChanges = (activate = false) =>
    run(async () => {
      const r = await client.PUT("/organizations/{organizationId}/scim", {
        params,
        body: { ...draft, activate },
      });
      if (r.error) {
        if (r.response.status === 409) await mutate();
        throw new Error(r.error.message);
      }
      if (r.data.currentUserRole !== "admin") {
        setLostAdmin(true);
        setCredential("");
      }
      setSaved(true);
      setPolicy(undefined);
      await mutate(r.data.configuration, { revalidate: false });
      if (r.data.currentUserRole === "admin") await Promise.all([refreshUsers(), refreshEvents()]);
    });

  if (lostAdmin)
    return (
      <Card variant="section">
        <CardHeader>
          <CardTitle>SCIM policy applied</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p>
            The policy removed your admin access. An active user granted the admin role by the identity provider can manage
            this organization.
          </p>
          <Button onClick={() => window.location.reload()}>Refresh access</Button>
        </CardContent>
      </Card>
    );

  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>Directory provisioning (SCIM)</CardTitle>
        <CardDescription>
          Sync users from your identity provider. Choose whether administrators or the provider assign roles. SSO still handles sign-in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {saved && <p role="status">SCIM policy applied.</p>}
        {(error || configurationError || usersError || eventsError) && (
          <p role="alert" className="text-destructive">
            {error || String(configurationError || usersError || eventsError)}
          </p>
        )}
        {!configuration ? (
          <p>Loading provisioning settings…</p>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="scim-url">SCIM base URL</Label>
              <Input id="scim-url" readOnly value={configuration.baseUrl} />
            </div>
            <p className="text-sm">
              {configuration.configured
                ? configuration.activatedAt
                  ? "SCIM is active."
                  : "Setup in progress. Choose who manages roles, sync users and their access, then activate SCIM."
                : "Configure SSO and verify a domain first. Connecting SCIM preserves existing access until activation."}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button disabled={busy} onClick={rotate}>
                {configuration.credentialConfigured ? "Rotate credential" : "Generate credential"}
              </Button>
              {configuration.credentialConfigured && (
                <Button variant="outline" disabled={busy} onClick={revoke}>
                  Revoke credential
                </Button>
              )}
              <Button
                variant="outline"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    await Promise.all([mutate(), refreshUsers(), refreshEvents()]);
                  })
                }
              >
                Refresh directory
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Rotating replaces the previous credential immediately. Revoking stops synchronization and
              retains the current access policy.
            </p>
            {credential && (
              <div className="space-y-2 rounded border p-4">
                <Label htmlFor="scim-secret">Bearer credential, shown once</Label>
                <Input id="scim-secret" readOnly value={credential} autoComplete="off" />
                <p className="text-sm">
                  Copy this into your identity provider. It cannot be retrieved later.
                </p>
                <Button
                  variant="outline"
                  onClick={() =>
                    run(async () => {
                      await navigator.clipboard.writeText(credential);
                    })
                  }
                >
                  Copy credential
                </Button>
                <Button variant="ghost" onClick={() => setCredential("")}>
                  Dismiss
                </Button>
              </div>
            )}
            {configuration.configured && (
              <>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    SCIM controls membership and disables auto-join, invitations, and manual additions or removals. Activation is permanent. Directory changes apply immediately once active.
                  </p>
                  <p className="text-sm">
                    Activation and provider changes can remove your access and the last admin. Check that an administrator is provisioned before activating.
                  </p>
                  <div className="flex items-center gap-3">
                    <Switch id="scim-entitlements" checked={draft.directRoleAssignmentsDisabled} disabled={busy}
                      onCheckedChange={(checked) => edit({ ...draft, directRoleAssignmentsDisabled: checked })} />
                    <Label htmlFor="scim-entitlements">Manage roles through the identity provider</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {draft.directRoleAssignmentsDisabled
                      ? "Your identity provider assigns admin, member, or viewer. Console role edits are disabled. Enabling preserves current member roles as initial assignments."
                      : "Administrators assign roles in the Members section. Existing roles are preserved. New and reactivated users receive the default join role. SCIM role writes are rejected."}
                  </p>
                  {draft.directRoleAssignmentsDisabled !== configuration.directRoleAssignmentsDisabled && (
                    <p role="status" className="text-sm">
                      Save to change who manages roles. Review the role policy before applying this change.
                    </p>
                  )}
                  <Button disabled={busy} onClick={() => applyChanges()}>
                    Save role policy
                  </Button>
                </div>
                {!configuration.activatedAt && (
                  <Button
                    disabled={busy || !configuration.credentialConfigured}
                    onClick={() => applyChanges(true)}
                  >
                    Activate SCIM
                  </Button>
                )}
                <div className="space-y-3">
                  <h3 className="font-medium">Provisioned users</h3>
                  <p className="text-sm">
                    Roles below show directory eligibility. They take effect once SCIM is activated.
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="text-left">Email</th>
                        <th className="text-left">Status</th>
                        <th className="text-left">Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users?.items.map((u) => (
                        <tr key={u.id} className="border-t">
                          <td className="py-2">{u.email}</td>
                          <td>{!u.active ? "Deactivated" : !u.role ? "No assigned role" : "Active"}</td>
                          <td>{u.role || "No access"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Button
                    variant="outline"
                    disabled={userStart === 1 || busy}
                    onClick={() => setUserStart(Math.max(1, userStart - 25))}
                  >
                    Previous users
                  </Button>{" "}
                  <Button
                    variant="outline"
                    disabled={!users || userStart + 25 > users.total || busy}
                    onClick={() => setUserStart(userStart + 25)}
                  >
                    Next users
                  </Button>
                </div>
                <div className="space-y-3">
                  <h3 className="font-medium">Provisioning history</h3>
                  <ul className="space-y-2 text-sm">
                    {events?.items.map((e) => (
                      <li key={e.id}>
                        <time>{new Date(e.createdAt).toLocaleString()}</time> · {e.operation} {e.resource} ·{" "}
                        {e.status}
                        <br />
                        {e.detail}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="outline"
                    disabled={eventStart === 1 || busy}
                    onClick={() => setEventStart(Math.max(1, eventStart - 25))}
                  >
                    Previous events
                  </Button>{" "}
                  <Button
                    variant="outline"
                    disabled={!events || eventStart + 25 > events.total || busy}
                    onClick={() => setEventStart(eventStart + 25)}
                  >
                    Next events
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
